import type { IconName } from "@/components/admin/icon";

export interface MarketingNavItem {
	to: string;
	label: string;
	icn: IconName;
	/** Correspondance exacte de l'URL (pour la racine de la section). */
	end?: boolean;
	/** Permissions (OU logique) ; absentes = ouvert à tout authentifié. */
	permissions?: string[];
	/** Préfixes d'URL supplémentaires qui gardent l'entrée active. */
	also?: string[];
}

export interface MarketingNavGroup {
	group: string;
	items: MarketingNavItem[];
}

/** Permissions du domaine, répétées pour la lisibilité des déclarations. */
const CMS_READ = ["MARKETING.CMS.READ", "MARKETING.CMS.WRITE"];
const CMS_WRITE = ["MARKETING.CMS.WRITE", "SYSTEM.SETTINGS.WRITE"];

/**
 * Sous-navigation de « Marketing & Contenus ».
 *
 * Dix-sept écrans qui vivaient à huit endroits de la barre latérale et deux
 * dans « Commerce ». Les regrouper n'était pas qu'une question d'esthétique :
 * on préparait une promotion dans un coin, la bannière qui l'annonce dans un
 * autre, et le SMS qui la pousse dans un troisième, sans jamais voir les trois
 * ensemble.
 *
 * Quatre familles plutôt qu'une liste : « ce que le site montre », « ce qu'on
 * envoie », « ce qu'on offre », « à qui et comment on est trouvé ».
 */
export const MARKETING_NAV: MarketingNavGroup[] = [
	{
		group: "Contenu site",
		items: [
			{
				to: "/marketing",
				label: "Vue d'ensemble",
				icn: "chart",
				end: true,
				permissions: CMS_READ,
			},
			{
				to: "/marketing/bannieres",
				label: "Bannières",
				icn: "layout",
				permissions: CMS_READ,
			},
			{
				to: "/cms",
				label: "Page d'accueil",
				icn: "home",
				end: true,
				permissions: CMS_WRITE,
			},
			{
				to: "/cms/pages",
				label: "Pages d'information",
				icn: "shield",
				permissions: CMS_WRITE,
			},
			{
				to: "/cms/engagements",
				label: "Engagements",
				icn: "check",
				permissions: CMS_WRITE,
			},
			{
				to: "/marketing/mag",
				label: "Le Mag",
				icn: "edit",
				permissions: CMS_READ,
			},
		],
	},
	{
		group: "Campagnes",
		items: [
			{
				to: "/marketing/newsletters",
				label: "Newsletters",
				icn: "mail",
				permissions: CMS_READ,
			},
			{
				to: "/marketing/push",
				label: "Push app",
				icn: "bell",
				permissions: CMS_READ,
			},
			{
				to: "/marketing/whatsapp",
				label: "WhatsApp",
				icn: "chat",
				permissions: CMS_READ,
			},
			{
				to: "/reseaux-sociaux",
				label: "Réseaux sociaux",
				icn: "users",
				permissions: ["MARKETING.CMS.WRITE"],
			},
			{
				to: "/publicites",
				label: "Publicités",
				icn: "trend",
				permissions: ["MARKETING.CMS.WRITE"],
			},
			{
				to: "/marketing/generateur",
				label: "Générateur IA",
				icn: "star",
				permissions: ["MARKETING.CMS.WRITE"],
			},
			{
				to: "/marketing/calendrier",
				label: "Calendrier",
				icn: "calendar",
				permissions: CMS_READ,
			},
		],
	},
	{
		group: "Offres",
		items: [
			{
				to: "/promotions",
				label: "Promotions",
				icn: "tag",
				permissions: ["MARKETING.CMS.WRITE", "CATALOG.READ"],
			},
			{
				to: "/njangi",
				label: "Njangi",
				icn: "users",
				permissions: ["MARKETING.CMS.READ"],
			},
			{
				to: "/liens-courts",
				label: "Liens courts",
				icn: "zap",
				permissions: CMS_READ,
				also: ["/liens-courts/statistiques"],
			},
		],
	},
	{
		group: "Audience & visibilité",
		items: [
			{
				to: "/marketing/segments",
				label: "Segments",
				icn: "filter",
				permissions: ["CUSTOMERS.READ", "MARKETING.CMS.READ"],
			},
			{
				to: "/cms/referencement",
				label: "Référencement",
				icn: "search",
				permissions: CMS_WRITE,
			},
			{
				to: "/parametres/lancement",
				label: "Lancement / Site",
				icn: "flag",
				permissions: CMS_WRITE,
			},
		],
	},
];

/** Toutes les adresses de la section — sert à garder l'entrée de barre active. */
export const MARKETING_PATHS: string[] = MARKETING_NAV.flatMap((g) =>
	g.items.flatMap((i) => [i.to, ...(i.also ?? [])]),
);
